import React, { useState, useEffect } from 'react';

const ColorPicker = ({ colors, onColorsChange, onClose }) => {
    const [localColors, setLocalColors] = useState(colors);

    // Update local colors when prop changes
    useEffect(() => {
        setLocalColors(colors);
    }, [colors]);

    const handleColorChange = (colorKey, newValue) => {
        const updatedColors = {
            ...localColors,
            [colorKey]: newValue
        };
        setLocalColors(updatedColors);
    };

    const handleApply = () => {
        onColorsChange(localColors);
        onClose();
    };

    const handleReset = () => {
        const defaultColors = {
            blue: '#0366d6',
            green: '#22c55e',
            red: '#ff6b6b',
            yellow: '#ffcc00'
        };
        setLocalColors(defaultColors);
    };

    const handleCancel = () => {
        setLocalColors(colors); // Reset to original values
        onClose();
    };

    const getSettingColorName = (key) => {
        switch (key.toLowerCase()) {
            case 'blue':
                return 'Color of Points and Lines';
            case 'green':
                return 'Color when dragging Points';
            case 'yellow':
                return 'Color of center Point';
            case 'red':
                return 'Color of arrow direction';
            default:
                return key;
        }

    }
    

    return (
        <div className="color-picker-overlay">
            <div className="color-picker-modal">
                <div className="color-picker-header">
                    <h3>Customize Colors</h3>
                    <button 
                        className="control-btn close-btn" 
                        onClick={handleCancel}
                        title="Close"
                    >
                        <i className="fa fa-times"></i>
                    </button>
                </div>

                <div className="color-picker-content">
                    <div className="color-list">
                        {Object.entries(localColors).map(([colorKey, colorValue]) => (
                            <div key={colorKey} className="color-item">
                                <label className="color-label">
                                    {getSettingColorName(colorKey.charAt(0).toUpperCase() + colorKey.slice(1))}:
                                </label>
                                <div className="color-input-group">
                                    <input
                                        type="color"
                                        value={colorValue}
                                        onChange={(e) => handleColorChange(colorKey, e.target.value)}
                                        className="color-input"
                                        title={`Select ${colorKey} color`}
                                    />
                                    <input
                                        type="text"
                                        value={colorValue}
                                        onChange={(e) => handleColorChange(colorKey, e.target.value)}
                                        className="color-text-input"
                                        placeholder="#000000"
                                        pattern="^#[0-9A-Fa-f]{6}$"
                                        title="Enter hex color code"
                                    />
                                    <div 
                                        className="color-preview"
                                        style={{ backgroundColor: colorValue }}
                                        title={`Preview: ${colorValue}`}
                                    ></div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="color-picker-actions">
                    <button 
                        className="btn reset-btn"
                        onClick={handleReset}
                        title="Reset to default colors"
                    >
                        <i className="fa fa-refresh"></i> Reset
                    </button>
                    <button 
                        className="btn cancel-btn"
                        onClick={handleCancel}
                        title="Cancel changes"
                    >
                        <i className="fa fa-times"></i> Cancel
                    </button>
                    <button 
                        className="btn apply-btn"
                        onClick={handleApply}
                        title="Apply color changes"
                    >
                        <i className="fa fa-check"></i> Apply
                    </button>
                </div>
            </div>

            <style jsx>{`
                .color-picker-overlay {
                    position: fixed;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    background-color: rgba(0, 0, 0, 0.7);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    z-index: 9999;
                }

                .color-picker-modal {
                    background: white;
                    border-radius: 8px;
                    width: 400px;
                    max-width: 90vw;
                    max-height: 80vh;
                    overflow: hidden;
                    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
                }

                .color-picker-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 15px 20px;
                    border-bottom: 1px solid #e0e0e0;
                    background-color: #f8f9fa;
                }

                .color-picker-header h3 {
                    margin: 0;
                    font-size: 18px;
                    color: #333;
                }

                .color-picker-content {
                    padding: 20px;
                    max-height: 400px;
                    overflow-y: auto;
                }

                .color-list {
                    display: flex;
                    flex-direction: column;
                    gap: 15px;
                }

                .color-item {
                    display: flex;
                    flex-direction: column;
                    gap: 8px;
                }

                .color-label {
                    font-weight: 500;
                    color: #555;
                    font-size: 14px;
                }

                .color-input-group {
                    display: flex;
                    align-items: center;
                    gap: 10px;
                }

                .color-input {
                    width: 50px;
                    height: 35px;
                    border: 1px solid #ccc;
                    border-radius: 4px;
                    cursor: pointer;
                    padding: 0;
                }

                .color-text-input {
                    flex: 1;
                    height: 35px;
                    padding: 8px;
                    border: 1px solid #ccc;
                    border-radius: 4px;
                    font-family: monospace;
                    font-size: 13px;
                }

                .color-preview {
                    width: 35px;
                    height: 35px;
                    border: 2px solid #ddd;
                    border-radius: 4px;
                    flex-shrink: 0;
                }

                .color-picker-actions {
                    display: flex;
                    justify-content: flex-end;
                    gap: 16px;
                    padding: 15px 20px;
                    border-top: 1px solid #e0e0e0;
                    background-color: #f8f9fa;
                }

                .btn {
                    padding: 6px 12px;
                    border: none;
                    border-radius: 4px;
                    cursor: pointer;
                    font-size: 12px;
                    font-weight: 500;
                    display: flex;
                    align-items: baseline;
                    gap: 2px;
                    transition: background-color 0.2s ease;
                }

                .reset-btn {
                    background-color: #6c757d;
                    color: white;
                }

                .reset-btn:hover {
                    background-color: #5a6268;
                }

                .cancel-btn {
                    background-color: #dc3545;
                    color: white;
                }

                .cancel-btn:hover {
                    background-color: #c82333;
                }

                .apply-btn {
                    background-color: #28a745;
                    color: white;
                }

                .apply-btn:hover {
                    background-color: #218838;
                }

                .control-btn {
                    background: none;
                    border: none;
                    font-size: 16px;
                    cursor: pointer;
                    color: #666;
                    padding: 5px;
                    border-radius: 3px;
                    transition: color 0.2s ease, background-color 0.2s ease;
                }

                .control-btn:hover {
                    color: #333;
                    background-color: #f0f0f0;
                }
            `}</style>
        </div>
    );
};

export default ColorPicker;
